// This device's side of phone notifications: what it supports, asking for permission (only on a
// tap), and the browser's push subscription. The server never sees anything but the subscription
// (endpoint and keys), sent once to POST /api/push/subscribe.
import type { DeviceFacts } from './views/notifications';

/** This device's id on the server, so Settings can tell "this phone" from others. */
export const PUSH_DEVICE_KEY = 'hush-push-device';

export function deviceFacts(): DeviceFacts {
  const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  const nav = navigator as Navigator & { standalone?: boolean };
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches || nav.standalone === true;
  // iPadOS reports a Mac; a Mac with touch is an iPad.
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
  const permission = supported ? (Notification.permission as DeviceFacts['permission']) : 'default';
  return { supported, standalone, ios, permission };
}

export function storedDeviceId(): string | null {
  try {
    return localStorage.getItem(PUSH_DEVICE_KEY);
  } catch {
    return null;
  }
}

export function storeDeviceId(id: string | null) {
  try {
    if (id) localStorage.setItem(PUSH_DEVICE_KEY, id);
    else localStorage.removeItem(PUSH_DEVICE_KEY);
  } catch {
    // Private window: this phone shows as another device next time; harmless.
  }
}

const keyBytes = (b64: string) => {
  const s = atob(b64.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64.length % 4)) % 4));
  return Uint8Array.from(s, (c) => c.charCodeAt(0));
};

/** The live subscription on this device, if any. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

/**
 * Ask for permission (must run in a tap) and subscribe. Returns the subscription to send to the
 * server, or the permission answer when it is not granted.
 */
export async function subscribeDevice(publicKey: string): Promise<{ endpoint: string; keys: { p256dh: string; auth: string } } | 'denied' | 'default'> {
  const answer = await Notification.requestPermission();
  if (answer !== 'granted') return answer;
  const reg = await navigator.serviceWorker.ready;
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) }));
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  return { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } };
}

export async function unsubscribeDevice(): Promise<void> {
  const sub = await currentSubscription();
  await sub?.unsubscribe();
}
